import { aircraftMarketReputation, aircraftType } from '../data/aircraft';
import { airportById } from '../data/airports';
import { distanceKm, annuityPayment } from '../simulation/math';
import { bankLoanAnnualRate, bankLoanAverageWeeklyInterest, bankLoanCapacity, bankLoanLimit, bankLoanMonthlyInterest, BANK_LOAN_TERM_WEEKS } from '../simulation/credit';
import { isAircraftDelivered, leaseDeposit, LEASE_DELIVERY_DELAY_WEEKS, LEASE_MINIMUM_WEEKS, leaseWeeklyPayment } from '../simulation/leasing';
import { HUB_MAX_LEVEL, aircraftOperation, cabinWeightedSeats, estimateRouteStats, forecastRoute, fuelPriceIndex, hubBuildCost, hubUpgradeCost, hubWeeklyCostForSpecialization, routeLaunchCost } from '../simulation/economy';
import { dismissAssistant as dismissAssistantBase, isAssistantHired, recruitAssistant as recruitAssistantBase } from '../assistants';
import { ENGINEERING_ASSISTANT_ID, type EngineeringStrategy } from '../assistants/flightSpeed';
import { applyForListing, buybackShares, completeAudit, completeListing, issueShares, playerBuyCompanyShares, playerBuyShares, playerSellCompanyShares, playerSellShares, updateSharePrice } from './listing';
import { buildFuelStorage, buyFuel, buyFuelFuture, cancelFuelStorageQueue, extendFuelStorageFacility, sellFuel, sellFuelFuture, shortFuelFuture } from './fuel';
import { abandonAuctionForPlayer, minimizeAuction, placeBid, resolveAuction } from './auction';
import { declarePlayerDividend, investInVentureRound, startPlayerVentureRound } from './capitalMarkets';

import { emptyRouteStats } from './createGame';
import { clamp } from '../simulation/math';
import type { AcquisitionType, AircraftAsset, CabinConfig, GameState, HubAsset, PurchaseMethod } from './types';
export { resolveMarketEventResponse } from './eventResponses';

export function declarePlayerDividendCommand(state: GameState, period: 'quarterly' | 'annual', payoutRatio: number): GameState {
  return declarePlayerDividend(state, period, payoutRatio);
}

export function startPlayerVentureRoundCommand(state: GameState, targetAmount: number, secondarySaleAmount: number): GameState {
  return startPlayerVentureRound(state, targetAmount, secondarySaleAmount);
}

export function investInVentureRoundCommand(state: GameState, roundId: string, amount: number): GameState {
  return investInVentureRound(state, roundId, amount);
}

const clone = <T,>(value: T): T => structuredClone(value);
export const LOW_MARGIN_SCAN_COST = 10_000;

function id(next: GameState, prefix: string): string {
  const value = `${prefix}-${next.nextId}`;
  next.nextId += 1;
  return value;
}

function touch(next: GameState): GameState {
  next.updatedAt = Date.now();
  next.ledger = next.ledger.slice(-1200);
  return next;
}

export function purchaseAircraft(
  state: GameState,
  typeId: string,
  acquisition: AcquisitionType,
  method: PurchaseMethod,
): GameState {
  const next = clone(state);
  const type = aircraftType(typeId);
  const requiredReputation = aircraftMarketReputation(typeId, state.week);
  if (acquisition === 'leased') throw new Error('请使用飞机租赁入口');
  if (state.reputation < requiredReputation) throw new Error(`声誉达到 ${requiredReputation} 后才有资格采购 ${type.name}`);
  if (acquisition === 'used' && method === 'finance') throw new Error('当前银行只为新飞机提供长期贷款');

  let purchasePrice = Math.round(type.price * (acquisition === 'used' ? 0.62 : 1));
  if (next.vouchers?.aircraftDiscount70) {
    purchasePrice = Math.round(purchasePrice * 0.7);
    next.vouchers.aircraftDiscount70 = undefined;
  }
  const condition = acquisition === 'used' ? 0.82 : 1;
  const aircraftId = id(next, 'aircraft');
  let cashOut = purchasePrice;
  let loanId: string | undefined;
  let financingFee = 0;

  if (method === 'finance') {
    const downPayment = purchasePrice * 0.25;
    const principal = purchasePrice - downPayment;
    financingFee = purchasePrice * 0.01;
    cashOut = downPayment + financingFee;
    loanId = id(next, 'loan');
    next.loans.push({
      id: loanId,
      kind: 'aircraft',
      aircraftId,
      originalPrincipal: principal,
      principalRemaining: principal,
      annualRate: 0.085,
      weeklyPayment: annuityPayment(principal, 0.085, 416),
      weeksRemaining: 416,
    });
    next.ledger.push({
      id: id(next, 'ledger'), week: next.week, category: 'debt_draw', cashAmount: principal,
      profitAmount: 0, objectId: loanId, label: `${type.name} 购机贷款`,
    });
  }

  if (next.cash < cashOut) throw new Error(`现金不足，还差 ${(cashOut - next.cash).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`);
  next.cash -= cashOut;
  next.aircraft.push({
    id: aircraftId,
    typeId,
    acquisition,
    condition,
    purchasePrice,
    bookValue: purchasePrice,
    acquiredWeek: next.week,
    weeksOwned: 0,
    totalCycles: 0,
    totalFlightHours: 0,
    speedModifier: 1,
    fareRatio: 100,
    crewSalaryModifier: 1,
    crewMood: 60,
    crewHiredWeek: next.week,
    routeIds: [],
    loanId,
  });
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'capex', cashAmount: -purchasePrice,
    profitAmount: 0, objectId: aircraftId, label: `购买${acquisition === 'used' ? '二手' : '新'} ${type.name}`,
  });
  if (financingFee) {
    next.ledger.push({
      id: id(next, 'ledger'), week: next.week, category: 'interest', cashAmount: -financingFee,
      profitAmount: -financingFee, objectId: loanId, label: `${type.name} 贷款设立费用`,
    });
  }
  next.quizCounter = 0;
  return touch(next);
}

export function leaseAircraft(state: GameState, typeId: string): GameState {
  const next = clone(state);
  const type = aircraftType(typeId);
  const requiredReputation = aircraftMarketReputation(typeId, state.week);
  if (state.reputation < requiredReputation) throw new Error(`声誉达到 ${requiredReputation} 后才有资格租赁 ${type.name}`);
  const deposit = leaseDeposit(type);
  if (next.cash < deposit) throw new Error(`现金不足以支付租赁押金，还差 ${(deposit - next.cash).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`);

  const aircraftId = id(next, 'aircraft');
  next.cash -= deposit;
  next.aircraft.push({
    id: aircraftId,
    typeId,
    acquisition: 'leased',
    condition: 1,
    purchasePrice: 0,
    bookValue: 0,
    acquiredWeek: next.week,
    weeksOwned: 0,
    totalCycles: 0,
    totalFlightHours: 0,
    speedModifier: 1,
    fareRatio: 100,
    crewSalaryModifier: 1,
    crewMood: 60,
    crewHiredWeek: next.week,
    routeIds: [],
    lease: {
      weeklyPayment: leaseWeeklyPayment(type),
      deposit,
      deliveryWeek: next.week + LEASE_DELIVERY_DELAY_WEEKS,
      minimumWeeks: LEASE_MINIMUM_WEEKS,
    },
  });
  next.ledger.push({
    id: id(next, 'ledger'),
    week: next.week,
    category: 'operating_expense',
    cashAmount: -deposit,
    profitAmount: 0,
    objectId: aircraftId,
    label: `租赁 ${type.name} 押金`,
  });
  next.quizCounter = 0;
  return touch(next);
}

export function borrowBankLoan(state: GameState, amount: number): GameState {
  const next = clone(state);
  const requested = Math.floor(amount);
  if (!Number.isFinite(requested) || requested <= 0) throw new Error('请输入有效的贷款金额');
  const capacity = bankLoanCapacity(next);
  if (requested > capacity) throw new Error(`超过当前银行授信额度，还可借 ${(capacity).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`);

  const annualRate = bankLoanAnnualRate(next);
  const loanId = id(next, 'loan');
  next.cash += requested;
  next.loans.push({
    id: loanId,
    kind: 'bank',
    originalPrincipal: requested,
    principalRemaining: requested,
    annualRate,
    weeklyPayment: bankLoanAverageWeeklyInterest(requested, annualRate),
    weeksRemaining: BANK_LOAN_TERM_WEEKS,
  });
  next.ledger.push({
    id: id(next, 'ledger'),
    week: next.week,
    category: 'debt_draw',
    cashAmount: requested,
    profitAmount: 0,
    objectId: loanId,
    label: '取得银行流动资金贷款',
  });
  return touch(next);
}

export function extendBankLoan(state: GameState, loanId: string): GameState {
  const next = clone(state);
  const loan = next.loans.find((item) => item.id === loanId);
  if (!loan) throw new Error('没有找到贷款');
  if (loan.kind !== 'bank') throw new Error('只有银行流动资金贷款可以展期');
  if (loan.weeksRemaining > 0) throw new Error('贷款到期后才能展期');
  const debtExcludingLoan = next.loans
    .filter((item) => item.id !== loan.id)
    .reduce((sum, item) => sum + item.principalRemaining, 0);
  if (debtExcludingLoan + loan.principalRemaining > bankLoanLimit(next)) throw new Error('当前授信额度不足，无法展期这笔贷款');

  const fee = Math.ceil(bankLoanMonthlyInterest(loan.principalRemaining, loan.annualRate));
  if (next.cash < fee) throw new Error('现金不足以支付展期手续费');
  next.cash -= fee;
  loan.weeksRemaining = BANK_LOAN_TERM_WEEKS;
  loan.weeklyPayment = bankLoanAverageWeeklyInterest(loan.principalRemaining, loan.annualRate);
  next.ledger.push({
    id: id(next, 'ledger'),
    week: next.week,
    category: 'interest',
    cashAmount: -fee,
    profitAmount: -fee,
    objectId: loanId,
    label: '银行贷款展期手续费',
  });
  return touch(next);
}

export function openRoute(
  state: GameState,
  originId: string,
  destinationId: string,
  aircraftId: string,
  _legacyStrategy?: unknown,
): GameState {
  const next = clone(state);
  const origin = airportById(originId);
  const destination = airportById(destinationId);
  if (originId === destinationId) throw new Error('起点和终点不能相同');
  if (!next.hubs.some((hub) => hub.airportId === originId)) throw new Error('新航线必须从现有枢纽出发');
  if (next.routes.some((route) => route.active && [route.originId, route.destinationId].includes(originId) && [route.originId, route.destinationId].includes(destinationId))) {
    throw new Error('这组城市之间已经有航线');
  }
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到所选飞机');
  if (aircraft.routeIds.length) throw new Error('所选飞机已经分配给其他航线');
  if (!isAircraftDelivered(aircraft, next.week)) throw new Error('租赁飞机下周交付后才能投入运营');
  const type = aircraftType(aircraft.typeId);
  const distance = distanceKm(origin, destination);
  if (distance > type.rangeKm) throw new Error(`${type.name} 航程不足`);
  if (type.runwayClass > origin.runwayClass || type.runwayClass > destination.runwayClass) throw new Error('机场跑道等级不支持该机型');

  const launchInvestment = routeLaunchCost(origin, destination);
  if (next.cash < launchInvestment) throw new Error('现金不足以支付航线申请、营销和机场押金');
  const routeId = id(next, 'route');
  const preview = forecastRoute(next, originId, destinationId, aircraft);
  next.cash -= launchInvestment;
  aircraft.fareRatio = 100;
  aircraft.routeIds.push(routeId);
  next.routes.push({
    id: routeId,
    originId,
    destinationId,
    aircraftIds: [aircraftId],
    openedWeek: next.week,
    maturityWeeks: 0,
    launchInvestment,
    active: true,
    reputationScore: 25,
    fareRatio: 100,
    stats: {
      ...emptyRouteStats(distance),
      seats: preview.seats,
      demand: preview.demand,
      passengers: preview.passengers,
      localPassengers: preview.passengers,
      loadFactor: preview.loadFactor,
      unmetDemand: Math.max(0, preview.demand - preview.passengers),
      revenue: preview.revenue,
      cashCost: preview.cashCost,
      operatingProfit: preview.weeklyProfit,
    },
  });
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'capex', cashAmount: -launchInvestment,
    profitAmount: 0, objectId: routeId, label: `开通 ${originId}-${destinationId}`,
  });
  next.quizCounter = 0;
  return touch(next);
}

export function assignAircraft(state: GameState, routeId: string, aircraftId: string): GameState {
  const next = clone(state);
  const route = next.routes.find((item) => item.id === routeId && item.active);
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!route || !aircraft) throw new Error('没有找到航线或飞机');
  if (aircraft.routeIds.length) throw new Error('这架飞机正在执行其他航线');
  if (!isAircraftDelivered(aircraft, next.week)) throw new Error('租赁飞机下周交付后才能投入运营');
  const type = aircraftType(aircraft.typeId);
  const origin = airportById(route.originId);
  const destination = airportById(route.destinationId);
  const distance = distanceKm(origin, destination);
  if (distance > type.rangeKm || type.runwayClass > origin.runwayClass || type.runwayClass > destination.runwayClass) {
    throw new Error('这架飞机不适合该航线');
  }
  const deploymentCost = 80_000 + Math.round(distance * 15);
  if (next.cash < deploymentCost) throw new Error('现金不足以完成飞机调度与航线准备');
  next.cash -= deploymentCost;
  aircraft.fareRatio = route.fareRatio ?? 100;
  aircraft.routeIds.push(route.id);
  route.aircraftIds.push(aircraft.id);
  route.stats = estimateRouteStats(next, route);
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'operating_expense', cashAmount: -deploymentCost,
    profitAmount: -deploymentCost, objectId: route.id, label: `向 ${route.originId}-${route.destinationId} 增派飞机`,
  });
  next.quizCounter = 0;
  return touch(next);
}

export function replaceAircraftOnRoute(state: GameState, routeId: string, fromAircraftId: string, toAircraftId: string): GameState {
  const next = clone(state);
  const route = next.routes.find((item) => item.id === routeId && item.active);
  const currentAircraft = next.aircraft.find((item) => item.id === fromAircraftId);
  const replacementAircraft = next.aircraft.find((item) => item.id === toAircraftId);
  if (!route || !currentAircraft || !replacementAircraft) throw new Error('没有找到航线或飞机');
  if (!currentAircraft.routeIds.includes(route.id) || !route.aircraftIds.includes(currentAircraft.id)) throw new Error('当前飞机不在该航线上');
  if (replacementAircraft.routeIds.length) throw new Error('替换飞机正在执行其他航线');
  if (!isAircraftDelivered(replacementAircraft, next.week)) throw new Error('租赁飞机下周交付后才能投入运营');

  const type = aircraftType(replacementAircraft.typeId);
  const origin = airportById(route.originId);
  const destination = airportById(route.destinationId);
  const distance = distanceKm(origin, destination);
  if (distance > type.rangeKm || type.runwayClass > origin.runwayClass || type.runwayClass > destination.runwayClass) {
    throw new Error('替换飞机不适合该航线');
  }

  const deploymentCost = 80_000 + Math.round(distance * 15);
  if (next.cash < deploymentCost) throw new Error('现金不足以完成飞机调度与航线准备');
  next.cash -= deploymentCost;
  currentAircraft.routeIds = currentAircraft.routeIds.filter((item) => item !== route.id);
  replacementAircraft.fareRatio = route.fareRatio ?? 100;
  replacementAircraft.routeIds.push(route.id);
  route.aircraftIds = route.aircraftIds.map((item) => (item === currentAircraft.id ? replacementAircraft.id : item));
  route.stats = estimateRouteStats(next, route);
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'operating_expense', cashAmount: -deploymentCost,
    profitAmount: -deploymentCost, objectId: route.id, label: `替换 ${route.originId}-${route.destinationId} 执飞飞机`,
  });
  next.quizCounter = 0;
  return touch(next);
}

export function unassignAircraft(state: GameState, routeId: string, aircraftId: string): GameState {
  const next = clone(state);
  const route = next.routes.find((item) => item.id === routeId && item.active);
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!route || !aircraft) throw new Error('没有找到航线或飞机');
  if (!aircraft.routeIds.includes(route.id) || !route.aircraftIds.includes(aircraft.id)) throw new Error('这架飞机不在该航线上');

  route.aircraftIds = route.aircraftIds.filter((item) => item !== aircraft.id);
  aircraft.routeIds = aircraft.routeIds.filter((item) => item !== route.id);
  route.stats = estimateRouteStats(next, route);
  return touch(next);
}

export function closeRoute(state: GameState, routeId: string): GameState {
  const next = clone(state);
  const route = next.routes.find((item) => item.id === routeId && item.active);
  if (!route) throw new Error('航线不存在或已经关闭');
  route.active = false;
  route.aircraftIds.forEach((aircraftId) => {
    const aircraft = next.aircraft.find((item) => item.id === aircraftId);
    if (aircraft) aircraft.routeIds = aircraft.routeIds.filter((item) => item !== route.id);
  });
  const refund = Math.round(route.launchInvestment * 0.12);
  next.cash += refund;
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'asset_sale', cashAmount: refund,
    profitAmount: 0, objectId: route.id, label: `关闭 ${route.originId}-${route.destinationId}，退回部分押金`,
  });
  return touch(next);
}

export function runLowMarginScan(state: GameState): GameState {
  const next = clone(state);
  if (next.cash < LOW_MARGIN_SCAN_COST) throw new Error('现金不足以执行低边际收益扫描');
  next.cash -= LOW_MARGIN_SCAN_COST;
  next.ledger.push({
    id: id(next, 'ledger'),
    week: next.week,
    category: 'operating_expense',
    cashAmount: -LOW_MARGIN_SCAN_COST,
    profitAmount: -LOW_MARGIN_SCAN_COST,
    label: '低边际收益扫描服务',
  });
  return touch(next);
}

export function buildHub(state: GameState, airportId: string): GameState {
  const next = clone(state);
  const airport = airportById(airportId);
  if (next.hubs.some((hub) => hub.airportId === airportId)) throw new Error('这里已经是枢纽');
  if (!next.routes.some((route) => route.active && [route.originId, route.destinationId].includes(airportId))) {
    throw new Error('只能把已经通航的城市升级为枢纽');
  }
  const cost = Math.round(hubBuildCost(airport) * (next.vouchers?.hubDiscount70 ? 0.7 : 1));
  if (next.vouchers?.hubDiscount70) next.vouchers.hubDiscount70 = undefined;
  if (next.cash < cost) throw new Error('现金不足以建设枢纽');
  next.cash -= cost;
  next.hubs.push({ airportId, level: 1, weeklyCost: hubWeeklyCostForSpecialization(airport, 1, 'balanced'), builtWeek: next.week, specialization: 'balanced' });
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'capex', cashAmount: -cost,
    profitAmount: 0, objectId: airportId, label: `建设 ${airportId} 枢纽`,
  });
  next.quizCounter = 0;
  return touch(next);
}

function refreshRoutesForHub(next: GameState, airportId: string): void {
  for (const route of next.routes.filter((item) => item.active && [item.originId, item.destinationId].includes(airportId))) {
    route.stats = estimateRouteStats(next, route);
  }
}

function activeRouteCountForHub(next: GameState, airportId: string): number {
  return next.routes.filter((route) => route.active && [route.originId, route.destinationId].includes(airportId)).length;
}

export function upgradeHub(state: GameState, airportId: string): GameState {
  const next = clone(state);
  const hub = next.hubs.find((item) => item.airportId === airportId);
  if (!hub) throw new Error('没有找到枢纽');
  if (hub.level >= HUB_MAX_LEVEL) throw new Error('枢纽已经达到最高等级');
  const airport = airportById(airportId);
  const nextLevel = hub.level + 1;
  const requiredRoutes = nextLevel * 3;
  const requiredReputation = 40 + nextLevel * 8;
  const routeCount = activeRouteCountForHub(next, airportId);
  if (routeCount < requiredRoutes) throw new Error(`升级到 L${nextLevel} 需要 ${requiredRoutes} 条相关航线`);
  if (next.reputation < requiredReputation) throw new Error(`升级到 L${nextLevel} 需要声誉 ${requiredReputation}`);
  const cost = hubUpgradeCost(airport, hub.level);
  if (next.cash < cost) throw new Error('现金不足以升级枢纽');
  next.cash -= cost;
  hub.level = nextLevel;
  hub.weeklyCost = hubWeeklyCostForSpecialization(airport, hub.level, hub.specialization);
  refreshRoutesForHub(next, airportId);
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'capex', cashAmount: -cost,
    profitAmount: 0, objectId: airportId, label: `升级 ${airportId} 枢纽至 L${hub.level}`,
  });
  return touch(next);
}

export function setHubSpecialization(state: GameState, airportId: string, specialization: HubAsset['specialization']): GameState {
  const next = clone(state);
  const hub = next.hubs.find((item) => item.airportId === airportId);
  if (!hub) throw new Error('没有找到枢纽');
  if (hub.specialization === specialization) return touch(next);
  const airport = airportById(airportId);
  const routeCount = activeRouteCountForHub(next, airportId);
  if (routeCount < 2 && specialization !== 'balanced') throw new Error('至少需要 2 条相关航线后才能启用专精');
  const cost = Math.round(hubBuildCost(airport) * 0.12 * hub.level);
  if (next.cash < cost) throw new Error('现金不足以切换枢纽专精');
  next.cash -= cost;
  hub.specialization = specialization;
  hub.weeklyCost = hubWeeklyCostForSpecialization(airport, hub.level, specialization);
  refreshRoutesForHub(next, airportId);
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'operating_expense', cashAmount: -cost,
    profitAmount: -cost, objectId: airportId, label: `调整 ${airportId} 枢纽专精`,
  });
  return touch(next);
}

export const COMPANY_NAME_MAX_LENGTH = 12;

export function renameCompany(state: GameState, name: string): GameState {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('公司名字不能为空');
  if ([...trimmed].length > COMPANY_NAME_MAX_LENGTH) throw new Error(`公司名字最多 ${COMPANY_NAME_MAX_LENGTH} 个字符`);
  if (trimmed === state.companyName) return state;
  const next = clone(state);
  next.companyName = trimmed;
  return touch(next);
}

export function sellAircraft(state: GameState, aircraftId: string): GameState {
  const next = clone(state);
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到飞机');
  if (aircraft.lease) throw new Error('租赁飞机不能出售，请使用退租');
  if (aircraft.routeIds.length) throw new Error('请先从航线撤下或关闭航线');
  const salePrice = Math.round(aircraft.bookValue * (0.72 + aircraft.condition * 0.18));
  const loan = aircraft.loanId ? next.loans.find((item) => item.id === aircraft.loanId) : undefined;
  const payoff = loan?.principalRemaining ?? 0;
  const proceeds = salePrice - payoff;
  next.cash += proceeds;
  next.aircraft = next.aircraft.filter((item) => item.id !== aircraftId);
  if (loan) next.loans = next.loans.filter((item) => item.id !== loan.id);
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'asset_sale', cashAmount: proceeds,
    profitAmount: salePrice - aircraft.bookValue, objectId: aircraftId, label: `出售 ${aircraftType(aircraft.typeId).name}${payoff ? '并结清贷款' : ''}`,
  });
  return touch(next);
}

export function returnLeasedAircraft(state: GameState, aircraftId: string): GameState {
  const next = clone(state);
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到飞机');
  if (!aircraft.lease) throw new Error('这架飞机不是租赁资产');
  if (aircraft.routeIds.length) throw new Error('请先从航线撤下或关闭航线');
  if (next.week < aircraft.acquiredWeek + aircraft.lease.minimumWeeks) {
    throw new Error(`最低租期 ${aircraft.lease.minimumWeeks} 周结束后才能退租`);
  }

  const refund = Math.round(aircraft.lease.deposit * 0.82);
  next.cash += refund;
  next.aircraft = next.aircraft.filter((item) => item.id !== aircraftId);
  next.ledger.push({
    id: id(next, 'ledger'),
    week: next.week,
    category: 'asset_sale',
    cashAmount: refund,
    profitAmount: 0,
    objectId: aircraftId,
    label: `退租 ${aircraftType(aircraft.typeId).name}，退回押金`,
  });
  return touch(next);
}

export function payOffLoan(state: GameState, loanId: string): GameState {
  const next = clone(state);
  const loan = next.loans.find((item) => item.id === loanId);
  if (!loan) throw new Error('没有找到贷款');
  const payoff = Math.ceil(loan.principalRemaining);
  if (next.cash < payoff) throw new Error('现金不足以提前结清这笔贷款');
  next.cash -= payoff;
  next.loans = next.loans.filter((item) => item.id !== loanId);
  const aircraft = next.aircraft.find((item) => item.loanId === loanId);
  if (aircraft) delete aircraft.loanId;
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'principal', cashAmount: -payoff,
    profitAmount: 0, objectId: loanId, label: aircraft ? '提前结清购机贷款' : '提前结清银行贷款',
  });
  return touch(next);
}

export function payDownBankLoan(state: GameState, loanId: string, amount: number): GameState {
  const next = clone(state);
  const loan = next.loans.find((item) => item.id === loanId);
  if (!loan) throw new Error('没有找到贷款');
  if (loan.kind !== 'bank') throw new Error('只有银行流动资金贷款支持部分结清');
  const payment = Math.floor(amount);
  if (!Number.isFinite(payment) || payment <= 0) throw new Error('请输入有效的还款金额');
  const minimumPayment = Math.ceil(loan.principalRemaining * 0.1);
  const payoff = Math.ceil(loan.principalRemaining);
  if (payment < minimumPayment && payment < payoff) throw new Error(`部分结清至少需要偿还 ${minimumPayment.toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`);
  if (payment > payoff) throw new Error('还款金额不能超过剩余本金');
  if (next.cash < payment) throw new Error('现金不足以完成本次还款');

  next.cash -= payment;
  loan.principalRemaining = Math.max(0, loan.principalRemaining - payment);
  if (loan.principalRemaining <= 1) {
    next.loans = next.loans.filter((item) => item.id !== loanId);
  } else {
    loan.weeklyPayment = bankLoanAverageWeeklyInterest(loan.principalRemaining, loan.annualRate);
  }
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'principal', cashAmount: -payment,
    profitAmount: 0, objectId: loanId, label: '部分结清银行贷款本金',
  });
  return touch(next);
}

export function refinanceLoan(state: GameState, loanId: string): GameState {
  const next = clone(state);
  const loan = next.loans.find((item) => item.id === loanId);
  if (!loan) throw new Error('没有找到贷款');
  const fee = Math.ceil(loan.principalRemaining * 0.01);
  if (next.cash < fee) throw new Error('现金不足以支付再融资手续费');
  loan.annualRate = Math.max(0.062, loan.annualRate - 0.018);
  loan.weeksRemaining = Math.max(156, loan.weeksRemaining + 52);
  loan.weeklyPayment = annuityPayment(loan.principalRemaining, loan.annualRate, loan.weeksRemaining);
  next.cash -= fee;
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'interest', cashAmount: -fee,
    profitAmount: -fee, objectId: loanId, label: loan.kind === 'bank' ? '银行贷款再融资手续费' : '购机贷款再融资手续费',
  });
  return touch(next);
}

function maintainAircraftInPlace(next: GameState, aircraftId: string): void {
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到飞机');
  const type = aircraftType(aircraft.typeId);
  const cost = aircraftMaintenanceCost(aircraft, next.aircraft);
  if (next.cash < cost) throw new Error('现金不足以执行维护');
  next.cash -= cost;
  aircraft.condition = Math.min(1, aircraft.condition + 0.16);
  next.ledger.push({
    id: id(next, 'ledger'), week: next.week, category: 'operating_expense', cashAmount: -cost,
    profitAmount: -cost, objectId: aircraftId, label: `${type.name} 维护检修`,
  });
}

export function maintainAircraft(state: GameState, aircraftId: string): GameState {
  const next = clone(state);
  maintainAircraftInPlace(next, aircraftId);
  return touch(next);
}

export function aircraftMaintenanceCostWithCount(aircraft: AircraftAsset, sameTypeCount: number): number {
  const type = aircraftType(aircraft.typeId);
  const maintenanceBase = aircraft.lease ? type.price * 0.42 : aircraft.purchasePrice;
  const baseCost = maintenanceBase * (0.006 + Math.max(0, 1 - aircraft.condition) * 0.018);
  const discount = Math.min((sameTypeCount - 1) * 0.01, 0.30);
  return Math.round(baseCost * (1 - Math.max(0, discount)));
}

export function aircraftMaintenanceCost(aircraft: AircraftAsset, fleet: AircraftAsset[] = []): number {
  const sameTypeCount = fleet.filter((item) => item.typeId === aircraft.typeId).length;
  return aircraftMaintenanceCostWithCount(aircraft, sameTypeCount);
}

export function estimateMaintenanceToThreshold(aircraft: AircraftAsset, sameTypeCount: number, threshold = 0.8): number {
  let cost = 0;
  let condition = aircraft.condition;
  while (condition < threshold && condition < 1) {
    const probe = { ...aircraft, condition };
    cost += aircraftMaintenanceCostWithCount(probe, sameTypeCount);
    condition = Math.round(Math.min(1, condition + 0.16) * 100) / 100;
  }
  return cost;
}

export function maintainLowConditionAircraft(state: GameState): GameState {
  const next = clone(state);
  const targets = next.aircraft.filter(
    (aircraft) =>
      isAircraftDelivered(aircraft, next.week) &&
      aircraft.condition < 0.8,
  );
  if (!targets.length) throw new Error('没有需要维护的飞机');
  const totalCost = targets.reduce((sum, aircraft) => sum + aircraftMaintenanceCost(aircraft, next.aircraft), 0);
  if (next.cash < totalCost) throw new Error('现金不足以执行一键维护');

  for (const aircraft of targets) {
    maintainAircraftInPlace(next, aircraft.id);
  }

  return touch(next);
}

export function setAircraftSpeedModifier(state: GameState, aircraftId: string, modifier: number): GameState {
  const next = clone(state);
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到飞机');
  aircraft.speedModifier = clamp(Number(modifier), 0.92, 1.08);
  for (const routeId of aircraft.routeIds) {
    const route = next.routes.find((item) => item.id === routeId);
    if (route) route.stats = estimateRouteStats(next, route);
  }
  return touch(next);
}

function strategyRoundTrips(state: GameState, aircraft: AircraftAsset, speed: number): number {
  const fuelIndex = fuelPriceIndex(state.week, state.seed);
  const distances = aircraft.routeIds
    .map((routeId) => state.routes.find((route) => route.id === routeId)?.stats.distanceKm ?? 0)
    .filter((distance) => distance > 0);
  if (!distances.length) return 0;
  return distances.reduce((sum, distance) => sum + aircraftOperation(aircraft, distance, fuelIndex, speed).roundTrips, 0);
}

function engineeringStrategySpeed(state: GameState, aircraft: AircraftAsset, strategy: EngineeringStrategy): number {
  if (!aircraft.routeIds.length) return aircraft.speedModifier ?? 1;
  const speeds = Array.from({ length: 17 }, (_, index) => Number((0.92 + index * 0.01).toFixed(2)));
  const currentSpeed = Number((aircraft.speedModifier ?? 1).toFixed(2));
  const baselineTrips = strategyRoundTrips(state, aircraft, 1);
  const fuelSavingSpeed = speeds.find((speed) => strategyRoundTrips(state, aircraft, speed) >= baselineTrips) ?? currentSpeed;
  if (strategy === 'flight') {
    return speeds.find((speed) => strategyRoundTrips(state, aircraft, speed) >= baselineTrips + 1) ?? fuelSavingSpeed;
  }
  return fuelSavingSpeed;
}

export function applyEngineeringStrategy(state: GameState, strategy: EngineeringStrategy): GameState {
  if (!isAssistantHired(state, ENGINEERING_ASSISTANT_ID)) throw new Error('工程助理尚未在岗');
  const next = clone(state);
  let changed = 0;
  for (const aircraft of next.aircraft) {
    const speed = engineeringStrategySpeed(next, aircraft, strategy);
    if (Math.abs(speed - (aircraft.speedModifier ?? 1)) < 0.001) continue;
    aircraft.speedModifier = speed;
    changed += 1;
  }
  for (const route of next.routes) {
    if (route.active) route.stats = estimateRouteStats(next, route);
  }
  next.events = [
    ...(next.events ?? []),
    {
      id: `event-${next.nextId++}`,
      week: next.week,
      tone: 'positive' as const,
      title: `工程助理：${strategy === 'flight' ? '优先航班' : '优先节油'}`,
      detail: changed ? `已调整 ${changed} 架飞机的巡航速度，重新计算航班与燃油成本。` : '当前机队已经符合该策略，无需调整。',
    },
  ].slice(-36);
  return touch(next);
}

export function recruitAssistant(state: GameState, assistantId: string): GameState {
  return touch(recruitAssistantBase(state, assistantId));
}

export function dismissAssistant(state: GameState, assistantId: string): GameState {
  return touch(dismissAssistantBase(state, assistantId));
}

export function completeAuditCommand(state: GameState): GameState {
  return touch(completeAudit(state));
}

export function applyForListingCommand(state: GameState): GameState {
  return touch(applyForListing(state));
}

export function completeListingCommand(state: GameState, issueRatio: number): GameState {
  return touch(completeListing(state, issueRatio));
}

export function issueSharesCommand(state: GameState, quantity: number): GameState {
  return touch(issueShares(state, quantity));
}

export function buybackSharesCommand(state: GameState, quantity: number): GameState {
  return touch(buybackShares(state, quantity));
}

export function playerSellSharesCommand(state: GameState, quantity: number): GameState {
  return touch(playerSellShares(state, quantity));
}

export function playerBuySharesCommand(state: GameState, quantity: number): GameState {
  return touch(playerBuyShares(state, quantity));
}

export function playerBuyCompanySharesCommand(state: GameState, companyId: string, quantity: number): GameState {
  return touch(playerBuyCompanyShares(state, companyId, quantity));
}

export function playerSellCompanySharesCommand(state: GameState, companyId: string, quantity: number): GameState {
  return touch(playerSellCompanyShares(state, companyId, quantity));
}

export function buildFuelStorageCommand(state: GameState, count = 1): GameState {
  return touch(buildFuelStorage(state, count));
}

export function cancelFuelStorageQueueCommand(state: GameState): GameState {
  return touch(cancelFuelStorageQueue(state));
}

export function extendFuelStorageFacilityCommand(state: GameState, facilityId: string): GameState {
  return touch(extendFuelStorageFacility(state, facilityId));
}

export function buyFuelCommand(state: GameState, gallons: number): GameState {
  return touch(buyFuel(state, gallons));
}

export function buyFuelFutureCommand(state: GameState, gallons: number, deliveryWeeks: number): GameState {
  return touch(buyFuelFuture(state, gallons, deliveryWeeks));
}

export function shortFuelFutureCommand(state: GameState, gallons: number, deliveryWeeks: number): GameState {
  return touch(shortFuelFuture(state, gallons, deliveryWeeks));
}

export function sellFuelCommand(state: GameState, gallons: number): GameState {
  return touch(sellFuel(state, gallons));
}

export function sellFuelFutureCommand(state: GameState, futureId: string): GameState {
  return touch(sellFuelFuture(state, futureId));
}

export function setAircraftCabinConfig(state: GameState, aircraftId: string, cabinConfig: CabinConfig): GameState {
  const next = clone(state);
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到飞机');
  const type = aircraftType(aircraft.typeId);
  const weighted = cabinWeightedSeats(cabinConfig);
  if (weighted > type.maxEconomySeats) throw new Error(`经济舱等效座位数不能超过 ${type.maxEconomySeats}`);
  if (cabinConfig.economy < 0 || cabinConfig.business < 0 || cabinConfig.first < 0) throw new Error('各舱位座位数不能为负');
  aircraft.cabinConfig = { ...cabinConfig };
  for (const routeId of aircraft.routeIds) {
    const route = next.routes.find((item) => item.id === routeId);
    if (route) route.stats = estimateRouteStats(next, route);
  }
  return touch(next);
}

export function setAircraftCrewSalaryModifier(state: GameState, aircraftId: string, modifier: number): GameState {
  const next = clone(state);
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到飞机');
  aircraft.crewSalaryModifier = clamp(Number(modifier), 0.20, 1.50);
  for (const routeId of aircraft.routeIds) {
    const route = next.routes.find((item) => item.id === routeId);
    if (route) route.stats = estimateRouteStats(next, route);
  }
  return touch(next);
}

export function setAllAircraftCrewSalaryModifier(state: GameState, modifier: number): GameState {
  const next = clone(state);
  const value = clamp(Number(modifier), 0.20, 1.50);
  for (const aircraft of next.aircraft) {
    aircraft.crewSalaryModifier = value;
  }
  for (const route of next.routes) {
    route.stats = estimateRouteStats(next, route);
  }
  return touch(next);
}

export function setRouteFareRatio(state: GameState, routeId: string, fareRatio: number): GameState {
  const next = clone(state);
  const route = next.routes.find((item) => item.id === routeId && item.active);
  if (!route) throw new Error('没有找到航线');
  const value = clamp(Number(fareRatio) || 0, 0, 400);
  route.pendingFareRatio = value;
  for (const aircraftId of route.aircraftIds) {
    const aircraft = next.aircraft.find((item) => item.id === aircraftId);
    if (aircraft) aircraft.pendingFareRatio = value;
  }
  return touch(next);
}

export function setAircraftFareRatio(state: GameState, aircraftId: string, fareRatio: number): GameState {
  const next = clone(state);
  const aircraft = next.aircraft.find((item) => item.id === aircraftId);
  if (!aircraft) throw new Error('没有找到飞机');
  aircraft.pendingFareRatio = clamp(Number(fareRatio) || 0, 0, 400);
  return touch(next);
}

export function placeAuctionBidCommand(state: GameState, incrementPercent: number): GameState {
  return placeBid(state, 'player', incrementPercent);
}

export function placeFixedAuctionBidCommand(state: GameState, amount: number): GameState {
  return placeBid(state, 'player', { amount });
}

export function abandonAuctionCommand(state: GameState): GameState {
  return abandonAuctionForPlayer(state);
}

export function minimizeAuctionCommand(state: GameState, minimized: boolean): GameState {
  return minimizeAuction(state, minimized);
}

export function resolveAuctionCommand(state: GameState): GameState {
  return resolveAuction(state);
}
